import {
  bandTiers,
  type MarkerKind,
  type ShareMarker,
  type SharePoint,
  tierLabel,
  type TierRef,
} from "@/lib/comms";

// Geometry ported 1:1 from the mockup's drawTrend.
const W = 560;
const H = 240;
const PAD_L = 38;
const PAD_R = 32;
const PAD_T = 46;
const PAD_B = 40;

/** "2026Q3" → "Q3 ’26" (mockup qLabel). */
function qLabel(quarter: string): string {
  return `Q${quarter.slice(5)} ’${quarter.slice(2, 4)}`;
}

interface TrendChartProps {
  series: SharePoint[];
  /** Aligned 1:1 with `series`. */
  markers: ShareMarker[];
  /**
   * The band the account sits in, drawn as dashed reference lines: the tier it
   * holds (null below T1) and the tier it is measured against. `bandTiers()`
   * collapses them to one line where the band has a single bound.
   */
  tierHeld: TierRef | null;
  targetTier: TierRef;
  /** Clicking the pulsing pending marker opens the draft drawer. */
  onPendingClick: () => void;
}

export function TrendChart({
  series,
  markers,
  tierHeld,
  targetTier,
  onPendingClick,
}: TrendChartProps) {
  if (series.length === 0) {
    return null;
  }

  const tiers = bandTiers(tierHeld, targetTier);

  const iw = W - PAD_L - PAD_R;
  const ih = H - PAD_T - PAD_B;
  const vals = series.map((p) => p.share);
  // The band's tiers are part of the domain, not just decoration on top of it:
  // an account at 56% has to be able to see how far 75% actually is.
  const bounds = [...vals, ...tiers.map((t) => t.value)];
  const vmin = Math.floor((Math.min(...bounds) - 5) / 5) * 5;
  let vmax = Math.ceil((Math.max(...bounds) + 6) / 5) * 5;
  if (vmax - vmin < 12) {
    vmax = vmin + 12;
  }
  const x = (i: number) => PAD_L + (series.length <= 1 ? 0 : (i / (series.length - 1)) * iw);
  const y = (v: number) => PAD_T + (1 - (v - vmin) / (vmax - vmin)) * ih;

  const lineD = series.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.share)}`).join(" ");
  const areaD = `${lineD} L ${x(series.length - 1)} ${H - PAD_B} L ${x(0)} ${H - PAD_B} Z`;
  const showEvery = series.length > 7 ? 2 : 1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="h-auto w-full">
      {[0, 1, 2, 3, 4].map((s) => {
        const val = vmin + ((vmax - vmin) * s) / 4;
        return (
          <g key={s}>
            <line
              x1={PAD_L}
              y1={y(val)}
              x2={W - PAD_R}
              y2={y(val)}
              stroke="var(--color-wg-03)"
              strokeWidth={1}
            />
            <text
              x={PAD_L - 8}
              y={y(val) + 4}
              textAnchor="end"
              fontSize={10}
              fill="var(--color-wg-06)"
            >
              {Math.round(val)}%
            </text>
          </g>
        );
      })}

      {tiers.map((t) => {
        const ty = y(t.value);
        return (
          <g key={t.tier}>
            <line
              x1={PAD_L}
              y1={ty}
              x2={W - PAD_R}
              y2={ty}
              stroke="var(--color-wg-05)"
              strokeWidth={1.2}
              strokeDasharray="4 4"
            />
            <text
              x={W - PAD_R}
              y={ty - 6}
              textAnchor="end"
              fontSize={9.5}
              fontWeight={600}
              fill="var(--color-wg-07)"
            >
              {tierLabel(t)}
            </text>
          </g>
        );
      })}

      <path d={areaD} fill="rgba(235,23,0,0.07)" />
      <path
        d={lineD}
        fill="none"
        stroke="var(--color-jj-red)"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {series.map((p, i) => {
        const cx = x(i);
        const cy = y(p.share);
        const kind: MarkerKind = markers[i]?.kind ?? "none";
        const isLast = i === series.length - 1;
        const anchor = cx > W - PAD_R - 30 ? "end" : "middle";
        const valueLabel = (
          <text
            x={anchor === "end" ? cx + 7 : cx}
            y={cy - 15}
            textAnchor={anchor}
            fontSize={12.5}
            fontWeight={700}
            fill="var(--color-jj-red)"
          >
            {p.share}%
          </text>
        );
        return (
          <g key={p.quarter}>
            {kind === "pending" ? (
              <>
                <circle cx={cx} cy={cy} r={7} fill="var(--color-jj-red)" opacity={0.3}>
                  <animate attributeName="r" values="7;18" dur="1.6s" repeatCount="indefinite" />
                  <animate
                    attributeName="opacity"
                    values="0.5;0"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle
                  cx={cx}
                  cy={cy}
                  r={7.5}
                  fill="#fff"
                  stroke="var(--color-jj-red)"
                  strokeWidth={2.4}
                />
                <text
                  x={cx}
                  y={cy + 3.6}
                  textAnchor="middle"
                  fontSize={9.5}
                  fontWeight={700}
                  fill="var(--color-jj-red)"
                >
                  ✱
                </text>
                {valueLabel}
                <circle
                  cx={cx}
                  cy={cy}
                  r={22}
                  fill="transparent"
                  role="button"
                  tabIndex={0}
                  aria-label="Review pending share email"
                  className="focus-visible:outline-jj-red cursor-pointer focus-visible:outline-2"
                  onClick={onPendingClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onPendingClick?.();
                    }
                  }}
                >
                  <title>Review pending share email</title>
                </circle>
              </>
            ) : kind === "sent" ? (
              <>
                <circle cx={cx} cy={cy} r={6.5} fill="var(--color-jj-red)" />
                <text
                  x={cx}
                  y={cy + 3.2}
                  textAnchor="middle"
                  fontSize={8.5}
                  fontWeight={700}
                  fill="#fff"
                >
                  ✱
                </text>
                {isLast && valueLabel}
              </>
            ) : kind === "skipped" ? (
              <>
                {/* Grey ✕: a draft existed and an operator declined it — not
                    the same story as "never nudged" (open marker below). */}
                <circle cx={cx} cy={cy} r={6.5} fill="var(--color-wg-06)" />
                <text
                  x={cx}
                  y={cy + 3.2}
                  textAnchor="middle"
                  fontSize={8.5}
                  fontWeight={700}
                  fill="#fff"
                >
                  ✕
                </text>
                {isLast && valueLabel}
                <title>Nudge skipped</title>
              </>
            ) : (
              <>
                <circle
                  cx={cx}
                  cy={cy}
                  r={4}
                  fill="#fff"
                  stroke="var(--color-jj-red)"
                  strokeWidth={2}
                />
                {isLast && valueLabel}
              </>
            )}
            {(i % showEvery === 0 || isLast) && (
              <text x={cx} y={H - 8} textAnchor="middle" fontSize={9.5} fill="var(--color-wg-07)">
                {qLabel(p.quarter)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
